import { useData } from '../../contexts/DataContext';
import { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslation } from 'react-i18next';
import { Link as ScrollLink } from 'react-scroll'
import { FaTools } from 'react-icons/fa';
import PageHero from '../../components/PageHero';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';

const Projects = () => {
  const { t } = useTranslation();
  const { data } = useData();
  const { language, updateDocumentTitle } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [imagesLoaded, setImagesLoaded] = useState({});
  const isRTL = language === 'ar';
  const location = useLocation();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('services');
  const { site_info, navigation, contact_info, social_media } = data;

  // Initialize services data
  const services = Array.isArray(data?.services)
    ? data.services
    : Object.values(data?.services || {});

  updateDocumentTitle(t('services.our_services'), site_info);

  const scrollConfig = {
    smooth: true,
    duration: 800, // Increased duration for smoother scroll
    spy: true,
    offset: -100, // Adjusted offset
    activeClass: 'active1',
    ignoreCancelEvents: false,
    spyThrottle: 500
  }

  // Set loading state based on data availability
  useEffect(() => {
    if (data) {
      setLoading(false);
    }
  }, [data]);

  // Handle scroll events
  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setIsScrolled(scrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isScrolled]);

  // Image loading handler
  const handleImageLoad = useCallback((imageId) => {
    setImagesLoaded(prev => ({
      ...prev,
      [imageId]: true
    }));
  }, []);

  // Navigation handler
  const handleNavigation = useCallback((item) => {
    setIsOpen(false);

    if (item.href?.startsWith('/service/')) {
      // First navigate to the service page
      navigate(item.href);
      // Then scroll to top
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      return;
    }

    if (item.to) {
      if (location.pathname !== '/') {
        navigate('/');
        requestAnimationFrame(() => {
          const element = document.getElementById(item.to);
          if (element) {
            const yOffset = -100;
            const yPosition = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top: yPosition, behavior: 'smooth' });
            setActiveSection(item.to);
          }
        });
      } else {
        const element = document.getElementById(item.to);
        if (element) {
          const yOffset = -100;
          const yPosition = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: yPosition, behavior: 'smooth' });
          setActiveSection(item.to);
        }
      }
    } else if (item.href) {
      navigate(item.href);
      setActiveSection(item.href);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location.pathname, navigate]);

  // Link content component
  const LinkContent = ({ item }) => (
    <div className="flex items-center">
      <span>{item.title}</span>
      {item.icon && <FontAwesomeIcon icon={item.icon} className="ml-2" />}
    </div>
  );

  // Modified NavigationItem component
  const NavigationItem = ({ item, className, children }) => {
    const location = useLocation();
    const isHomePage = location.pathname === '/';

    const handleClick = (e) => {
      e.preventDefault();
      if (item.href?.startsWith('/service/')) {
        navigate(item.href);
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      } else {
        handleNavigation(item);
      }
    };

    return (
      <div
        onClick={handleClick}
        className={className}
        role="button"
        tabIndex={0}
      >
        {children}
      </div>
    );
  };

  // Update handleNavigation to handle scroll restoration
  useEffect(() => {
    if (location.state?.scrollTo) {
      setTimeout(() => {
        const element = document.getElementById(location.state.scrollTo);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
        }
        // Clear the state
        navigate(location.pathname, {
          replace: true,
          state: {}
        });
      }, 100);
    }
  }, [location.state, navigate]);

  // Loading image component
  const LoadingImage = ({ src, alt, className, index, type }) => {
    const [isLoaded, setIsLoaded] = useState(false);

    return (
      <div className="relative w-full min-h-[350px] max-h-[350px]"> {/* Add minimum height container */}
        {!isLoaded && (
          <div className="absolute inset-0 bg-gray-200 rounded-lg min-h-[350px]"> {/* Add minimum height to placeholder */}
            <div className="flex justify-center items-center h-full">
              <svg className="w-8 h-8 text-gray-300" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </div>
          </div>
        )}
        <img
          src={src}
          alt={alt}
          className={`${className} ${isLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300 w-full min-h-[350px] max-h-[350px] object-cover`}
          onLoad={() => setIsLoaded(true)}
        />
      </div>
    );
  };

  // Modified learn more button component
  const LearnMoreButton = ({ isRTL }) => (
    <div className={`flex justify-end items-center mt-4`}>
      <div className={`inline-flex relative gap-2 items-center px-4 py-2 font-medium bg-gradient-to-r rounded-full transition-all duration-300 to-stone-100 from-stone-50 text-stone-800 group-hover:from-stone-100 group-hover:to-stone-200`}>
        <span className="relative z-10">{t('learn_more')}</span>
        <FontAwesomeIcon
          icon={faArrowRight}
          className={`
            transition-transform duration-300
            ${isRTL ? 'rotate-180 group-hover:-translate-x-2' : 'group-hover:translate-x-2'}
          `}
        />
        <div className="absolute inset-0 rounded-full opacity-0 transition-opacity duration-300 bg-stone-800 group-hover:opacity-10" />
      </div>
    </div>
  );

  return (
    <div className="relative">
      {loading && (
        <div className="flex absolute inset-0 z-50 justify-center items-center bg-white bg-opacity-75">
          <div className="w-12 h-12 rounded-full border-t-2 border-b-2 animate-spin border-stone-800"></div>
        </div>
      )}

      <PageHero type="services" />

      <section className="py-12 bg-gray-50">
        <div className="container px-4 mx-auto">
          <h1 className="mb-6 text-3xl font-bold">{t('services.our_services')}</h1>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {services.map((service, index) => (
              <NavigationItem
                key={service.slug}
                item={{
                  href: `/service/${service.slug}`
                }}
                className="block h-full cursor-pointer group"
              >
                <div className="flex overflow-hidden flex-col h-full bg-white rounded-lg shadow-md transition-all duration-300 group-hover:shadow-lg group-hover:transform group-hover:scale-105">
                  <div className="relative flex-shrink-0">
                    <LoadingImage
                      src={service.image}
                      alt={service.title}
                      className="object-cover w-full h-full"
                      index={index}
                      type="service-card"
                    />
                    <div className="flex absolute top-4 right-4 justify-center items-center w-12 h-12 bg-white rounded-full shadow-lg transition-transform duration-300 group-hover:scale-110">
                      {service.icon ? (
                        <i className={`${service.icon} text-xl text-stone-800`}></i>
                      ) : (
                        <FaTools className="text-xl text-stone-800" />
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col flex-grow p-6">
                    <div className="flex-grow">
                      <h3 className="mb-3 text-xl font-semibold text-gray-800 transition-colors duration-300 group-hover:text-stone-800">
                        {service.title}
                      </h3>
                      <p className="mb-4 text-sm text-gray-600 line-clamp-3">
                        {service.description.slice(0, 200)}
                      </p>
                    </div>
                    <LearnMoreButton isRTL={isRTL} />
                  </div>
                </div>
              </NavigationItem>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Projects;